import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import {
  Button,
  Card,
  Col,
  Form as BsForm,
  Row,
  Spinner,
} from 'react-bootstrap';
import { toast } from 'react-toastify';

import { lancarConferenciaFechamentoCaixa } from '~/Services/Service';
import { observacaoSchema } from '../validationSchema';

const extrairMensagemErro = (err, fallback) => {
  if (err && typeof err === 'object' && err.message) return err.message;
  return fallback;
};

const formatarMoeda = (valor) => {
  const n = Number(valor || 0);
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const SecaoConferencia = ({ filtros, calculo, onLancadoSucesso, onConflict }) => {
  const [lancando, setLancando] = useState(false);

  if (!calculo) return null;

  const diferenca = Number(calculo.diferenca || 0);
  const corDiferenca = diferenca >= 0 ? '#000080' : '#FF0000';

  const handleLancar = (values, { setSubmitting }) => {
    setLancando(true);
    const payload = {
      codigo_grupo_empresa: filtros.codigo_grupo_empresa,
      codigo_conta: filtros.codigo_conta,
      codigo_ativo: filtros.codigo_ativo,
      conta_diferenca: filtros.conta_diferenca,
      dinheiro: calculo.valoresDigitados.dinheiro,
      moeda: calculo.valoresDigitados.moeda,
      vale: calculo.valoresDigitados.vale,
      cheque: calculo.valoresDigitados.cheque,
      saldo_conta: calculo.saldoConta,
      diferenca: calculo.diferenca,
      observacao1: (values.observacao1 || '').trim(),
      observacao2: (values.observacao2 || '').trim(),
      observacao3: (values.observacao3 || '').trim(),
    };

    lancarConferenciaFechamentoCaixa(payload)
      .then((res) => {
        const msg =
          res && res.mensagem
            ? res.mensagem
            : 'Conferência lançada com sucesso';
        toast.success(msg);
        onLancadoSucesso();
      })
      .catch((err) => {
        const status = err && err.status;
        if (
          status === 409 ||
          (err && err.message && err.message.toLowerCase().includes('conflict'))
        ) {
          toast.error(
            'Processo já foi finalizado/cancelado por outro usuário. Recarregue a tela.',
            { autoClose: false }
          );
          onConflict();
          return;
        }
        toast.error(extrairMensagemErro(err, 'Erro ao lançar conferência'));
      })
      .finally(() => {
        setLancando(false);
        setSubmitting(false);
      });
  };

  return (
    <Card className="mb-3">
      <Card.Header>
        <strong>Conferência</strong>
      </Card.Header>
      <Card.Body>
        <Row className="mb-3">
          <Col xs={12} md={4}>
            <BsForm.Group>
              <small>Total Digitado</small>
              <div
                className="form-control form-control-sm bg-light"
                style={{ fontWeight: 600 }}
              >
                {formatarMoeda(calculo.totalDigitado)}
              </div>
            </BsForm.Group>
          </Col>
          <Col xs={12} md={4}>
            <BsForm.Group>
              <small>Saldo na Conta</small>
              <div
                className="form-control form-control-sm bg-light"
                style={{ fontWeight: 600 }}
              >
                {formatarMoeda(calculo.saldoConta)}
              </div>
            </BsForm.Group>
          </Col>
          <Col xs={12} md={4}>
            <BsForm.Group>
              <small>Diferença</small>
              <div
                className="form-control form-control-sm bg-light"
                style={{ fontWeight: 700, color: corDiferenca }}
              >
                {formatarMoeda(calculo.diferenca)}
              </div>
            </BsForm.Group>
          </Col>
        </Row>

        <Formik
          initialValues={{ observacao1: '', observacao2: '', observacao3: '' }}
          validationSchema={observacaoSchema}
          onSubmit={handleLancar}
        >
          {({ values, errors, touched, handleChange, isSubmitting, isValid }) => {
            const algumaObs = Boolean(
              (values.observacao1 || '').trim() ||
                (values.observacao2 || '').trim() ||
                (values.observacao3 || '').trim()
            );
            return (
              <Form>
                <Row>
                  <Col xs={12} md={4} className="mb-2">
                    <BsForm.Group controlId="observacao1">
                      <small>Observação 1</small>
                      <BsForm.Control
                        type="text"
                        size="sm"
                        name="observacao1"
                        maxLength={50}
                        value={values.observacao1}
                        onChange={handleChange}
                        isInvalid={
                          !!(errors.observacao1 && touched.observacao1)
                        }
                      />
                      <BsForm.Control.Feedback type="invalid">
                        {errors.observacao1}
                      </BsForm.Control.Feedback>
                    </BsForm.Group>
                  </Col>
                  <Col xs={12} md={4} className="mb-2">
                    <BsForm.Group controlId="observacao2">
                      <small>Observação 2</small>
                      <BsForm.Control
                        type="text"
                        size="sm"
                        name="observacao2"
                        maxLength={50}
                        value={values.observacao2}
                        onChange={handleChange}
                        isInvalid={
                          !!(errors.observacao2 && touched.observacao2)
                        }
                      />
                      <BsForm.Control.Feedback type="invalid">
                        {errors.observacao2}
                      </BsForm.Control.Feedback>
                    </BsForm.Group>
                  </Col>
                  <Col xs={12} md={4} className="mb-2">
                    <BsForm.Group controlId="observacao3">
                      <small>Observação 3</small>
                      <BsForm.Control
                        type="text"
                        size="sm"
                        name="observacao3"
                        maxLength={50}
                        value={values.observacao3}
                        onChange={handleChange}
                        isInvalid={
                          !!(errors.observacao3 && touched.observacao3)
                        }
                      />
                      <BsForm.Control.Feedback type="invalid">
                        {errors.observacao3}
                      </BsForm.Control.Feedback>
                    </BsForm.Group>
                  </Col>
                </Row>
                {!algumaObs && (
                  <div className="text-muted small mb-2">
                    Informe ao menos uma observação para habilitar o lançamento.
                  </div>
                )}
                <Button
                  type="submit"
                  variant="success"
                  size="sm"
                  disabled={!algumaObs || !isValid || isSubmitting || lancando}
                >
                  {lancando ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-1"
                      />
                      Lançando...
                    </>
                  ) : (
                    'Lançar Conferência'
                  )}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default SecaoConferencia;
